import { Lead } from "@/types";

// Function to extract field data from lead
export const getFieldData = (lead: Lead) => {
  const fullName = lead.data["Full Name"] || 
                  lead.data["first_name"] + " " + lead.data["last_name"] || 
                  "Unknown";
                  
  const email = lead.data["Email"] || 
               lead.data["email"] || 
               lead.data["Email Address"] || 
               "";
               
  const phone = lead.data["Phone"] || 
               lead.data["phone"] || 
               lead.data["Phone Number"] || 
               "";
               
  const location = lead.data["Location"] || 
                  lead.data["location"] || 
                  (lead.data["city"] && lead.data["state"] ? 
                    `${lead.data["city"]}, ${lead.data["state"]}` : "");
                    
  return {
    fullName,
    email,
    phone,
    location
  };
};

// Format date consistently without the year
export const formatDate = (date: Date): string => {
  // Format date as "Mar 28th" (abbreviated month and ordinal day)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  
  // Add ordinal suffix
  const dayWithSuffix = day + (day % 10 === 1 && day !== 11 ? 'st' : 
                               day % 10 === 2 && day !== 12 ? 'nd' : 
                               day % 10 === 3 && day !== 13 ? 'rd' : 'th');
  
  return `${month} ${dayWithSuffix}`;
};

// Format location data from different sources
export const formatLocation = (locationString: string, data: Record<string, any>): string => {
  if (locationString) return locationString;
  
  // Check for different location property combinations
  if (data._location_city || data._location_region) {
    const parts = [];
    if (data._location_city) parts.push(data._location_city);
    if (data._location_region) parts.push(data._location_region);
    return parts.join(", ");
  }
  
  return "";
};

// State abbreviation function
export const getStateAbbreviation = (stateName: string): string => {
  // Dictionary of US state names to their abbreviations
  const stateMap: Record<string, string> = {
    "alabama": "AL",
    "alaska": "AK",
    "arizona": "AZ",
    "arkansas": "AR",
    "california": "CA",
    "colorado": "CO",
    "connecticut": "CT",
    "delaware": "DE",
    "florida": "FL",
    "georgia": "GA",
    "hawaii": "HI",
    "idaho": "ID",
    "illinois": "IL",
    "indiana": "IN",
    "iowa": "IA",
    "kansas": "KS",
    "kentucky": "KY",
    "louisiana": "LA",
    "maine": "ME",
    "maryland": "MD",
    "massachusetts": "MA",
    "michigan": "MI",
    "minnesota": "MN",
    "mississippi": "MS",
    "missouri": "MO",
    "montana": "MT",
    "nebraska": "NE",
    "nevada": "NV",
    "new hampshire": "NH",
    "new jersey": "NJ",
    "new mexico": "NM",
    "new york": "NY",
    "north carolina": "NC",
    "north dakota": "ND",
    "ohio": "OH",
    "oklahoma": "OK",
    "oregon": "OR",
    "pennsylvania": "PA",
    "rhode island": "RI",
    "south carolina": "SC",
    "south dakota": "SD",
    "tennessee": "TN",
    "texas": "TX",
    "utah": "UT",
    "vermont": "VT",
    "virginia": "VA",
    "washington": "WA",
    "west virginia": "WV",
    "wisconsin": "WI",
    "wyoming": "WY",
    "district of columbia": "DC",
    "american samoa": "AS",
    "guam": "GU",
    "northern mariana islands": "MP",
    "puerto rico": "PR",
    "united states minor outlying islands": "UM",
    "u.s. virgin islands": "VI",
  };

  // Handle different formats of state names
  // If it's already an abbreviation (2 letters), return it
  if (/^[A-Z]{2}$/.test(stateName)) {
    return stateName;
  }

  // Try to find the abbreviation by state name
  const normalizedState = stateName.toLowerCase().trim();
  
  // If it's a direct match
  if (stateMap[normalizedState]) {
    return stateMap[normalizedState];
  }
  
  // Check if it starts with any state name (for cases like "California, USA")
  for (const [state, abbr] of Object.entries(stateMap)) {
    if (normalizedState.startsWith(state)) {
      return abbr;
    }
  }
  
  // If we can't find a match, return the original value
  return stateName;
};

// Format currency function
export const formatCurrency = (amount: number): string => {
  // Format as USD currency
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
